/**
 * AppShell — shared layout structure for Editor and Web apps.
 *
 * Provides the complete app layout:
 *   Toolbar → [Sidebar + (TabBar → Content → LogPanel)] → StatusBar
 *
 * Apps pass platform-specific elements via props (slots).
 */

import { useT } from "@magic-md/i18n/react";
import type { ComponentType, ReactNode } from "react";
import { memo, useEffect, useRef, useState } from "react";
import { CommandPalette } from "../components/CommandPalette";
import {
  DialogHost,
  HistorySnapshotDialogHost,
  HistoryUndoToastHost,
  ToastHost,
} from "../components/dialogs";
import { DocumentPublishDialogHost } from "../components/dialogs/DocumentPublishDialog";
import { FolderPublishDialogHost } from "../components/dialogs/FolderPublishDialog";
import { EngineDetailView } from "../components/EngineDetailView";
import { ExtensionDetailView } from "../components/ExtensionDetailView";
import { InstanceDetailView } from "../components/InstanceDetailView";
import { LogPanel } from "../components/LogPanel";
import { McpDetailView } from "../components/McpDetailView";
import { PluginDetailView } from "../components/PluginDetailView";
import { SessionDetailView } from "../components/SessionDetailView";
import type { ActivityItem, SidebarProps } from "../components/Sidebar";
import { Sidebar } from "../components/Sidebar";
import { SidecarDetailView } from "../components/SidecarDetailView";
import { StatusBar } from "../components/StatusBar";
import { SettingsCenter } from "../components/settings/SettingsCenter";
import { SettingsRuntimeBindings } from "../components/settings/SettingsRuntimeBindings";
import { TabBar } from "../components/TabBar";
import type { ToolbarProps } from "../components/Toolbar";
import { Toolbar } from "../components/Toolbar";
import { WelcomeScreen } from "../components/WelcomeScreen";
import { WorkbenchI18nProvider } from "../components/WorkbenchI18nProvider";
import { bindShortcut, registerCommand } from "../lib/commands";
import { getSidebarPresentation, useEditorStore } from "../store/editor-store";
import { ExtensionFloatingPanels } from "../extensions/ExtensionContributions";
import { PaneLayoutContainer } from "./PaneLayout";
import { TabDndProvider } from "./TabDndContext";

export interface AppShellProps {
  /** Extra elements rendered inside the Toolbar (e.g. PushButton) */
  toolbarSlot?: ReactNode;
  /** Props forwarded to Toolbar (dragging handlers) */
  toolbarProps?: Partial<ToolbarProps>;
  /** The engine connector button to render at the bottom of the sidebar */
  sidebarBottomSlot?: ReactNode;
  /** Panel component overrides for the Sidebar (e.g. FileManager) */
  sidebarPanelOverrides?: SidebarProps["panelOverrides"];
  /** Custom activity items — restricts which sidebar panels are shown */
  sidebarActivityItems?: ActivityItem[];
  /** The engine connector panel component — rendered when not undefined */
  EngineConnectorPanel?: ComponentType;
  /** Close handler for the engine connector panel when the user clicks away */
  onCloseEngineConnectorPanel?: () => void;
  /** Called when user drags a tab outside the window bounds */
  onDragOutOfWindow?: (tabId: string) => void;
  /** Hide the tab bar (e.g. detached single-file windows) */
  hideTabBar?: boolean;
}

export const AppShell = memo(function AppShell({
  ...props
}: AppShellProps) {
  return (
    <WorkbenchI18nProvider>
      <AppShellInner {...props} />
    </WorkbenchI18nProvider>
  );
});

const AppShellInner = memo(function AppShellInner({
  toolbarSlot,
  toolbarProps,
  sidebarBottomSlot,
  sidebarPanelOverrides,
  sidebarActivityItems,
  EngineConnectorPanel,
  onCloseEngineConnectorPanel,
  onDragOutOfWindow,
  hideTabBar,
}: AppShellProps) {
  const t = useT("editor");
  const hasTabs = useEditorStore((s) => s.tabs.length > 0);
  const isPaneLayoutActive = useEditorStore(
    (s) => Boolean(s.paneLayout && s.panes.length > 0),
  );
  const selectedPluginName = useEditorStore((s) => s.selectedPluginName);
  const selectedInstanceId = useEditorStore((s) => s.selectedInstanceId);
  const selectedSidecarId = useEditorStore((s) => s.selectedSidecarId);
  const selectedExtensionId = useEditorStore((s) => s.selectedExtensionId);
  const showEngineDetail = useEditorStore((s) => s.showEngineDetail);
  const showMcpDetail = useEditorStore((s) => s.showMcpDetail);
  const showSessionDetail = useEditorStore((s) => s.showSessionDetail);
  const sidebarOpen = useEditorStore((s) => s.sidebarOpen);
  const sidebarView = useEditorStore((s) => s.sidebarView);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const engineConnectorPopoverRef = useRef<HTMLDivElement | null>(null);
  const sidebarPresentation = getSidebarPresentation(sidebarView);
  const contentSidebarOpen = sidebarOpen && sidebarPresentation === "content";
  const showGlobalTabBar = !hideTabBar && !isPaneLayoutActive && !contentSidebarOpen;

  useEffect(() => {
    if (!EngineConnectorPanel || !onCloseEngineConnectorPanel) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      const popover = engineConnectorPopoverRef.current;
      if (popover?.contains(target)) return;

      const targetElement = target instanceof Element ? target : target.parentElement;
      if (targetElement?.closest("[data-engine-connector-trigger='true']")) return;

      onCloseEngineConnectorPanel();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [EngineConnectorPanel, onCloseEngineConnectorPanel]);

  // Wire the palette.open command to this AppShell's own state.
  // This must run after App-level useEffect so it overwrites the
  // common-commands placeholder with the real setPaletteOpen handler.
  useEffect(() => {
    registerCommand({
      id: "palette.open",
      label: t("app.commandPalette"),
      shortcut: "Cmd+Shift+P",
      category: t("category.general"),
      execute: () => setPaletteOpen(true),
    });
    bindShortcut("Cmd+Shift+P", "palette.open");
  }, [t]);

  return (
    <div
      className="flex flex-col h-full"
      data-testid="app-shell"
      data-app-ready="true"
    >
      <SettingsRuntimeBindings />
      <Toolbar {...toolbarProps} toolbarSlot={toolbarSlot} />

      <div className="flex flex-1 min-h-0 min-w-0">
        <Sidebar
          activityItems={sidebarActivityItems}
          panelOverrides={sidebarPanelOverrides}
          bottomSlot={sidebarBottomSlot}
        />

        {/* Right of sidebar: TabBar + Content + LogPanel */}
        <TabDndProvider onDragOutOfWindow={onDragOutOfWindow}>
          <div
            className="flex flex-col flex-1 min-w-0 min-h-0"
            data-magic-editor-preview-workspace="true"
          >
            {/* Global TabBar only when PaneLayout is not rendering per-pane tabs. */}
            {showGlobalTabBar && <TabBar />}

            <div className="flex flex-1 min-h-0 min-w-0 flex-col">
              <div className="relative flex flex-1 min-h-0 min-w-0">
                {/* Main content — always mounted to preserve pane state */}
                {!hasTabs ? <WelcomeScreen /> : <PaneLayoutContainer />}

                {contentSidebarOpen ? (
                  <div className="absolute inset-0 z-30 bg-background overflow-auto">
                    {sidebarView === "settings" ? <SettingsCenter /> : null}
                  </div>
                ) : null}

                {/* Detail view overlay — renders on top of panes */}
                {selectedSidecarId && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <SidecarDetailView sidecarId={selectedSidecarId} />
                  </div>
                )}
                {showSessionDetail && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <SessionDetailView />
                  </div>
                )}
                {showMcpDetail && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <McpDetailView />
                  </div>
                )}
                {showEngineDetail && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <EngineDetailView />
                  </div>
                )}
                {selectedExtensionId && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <ExtensionDetailView extensionId={selectedExtensionId} />
                  </div>
                )}
                {selectedPluginName && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <PluginDetailView pluginName={selectedPluginName} />
                  </div>
                )}
                {selectedInstanceId && (
                  <div className="absolute inset-0 z-40 bg-background overflow-auto">
                    <InstanceDetailView instanceId={selectedInstanceId} />
                  </div>
                )}
              </div>
              {/* Log panel — participates in layout so content remains reachable. */}
              <LogPanel />
            </div>
          </div>
        </TabDndProvider>
      </div>

      <StatusBar />
      <ExtensionFloatingPanels />

      {/* Engine Connector Popover */}
      {EngineConnectorPanel ? (
        <div
          ref={engineConnectorPopoverRef}
          data-testid="engine-connector-popover"
          className="fixed left-12 bottom-8 z-50 w-80 bg-background border border-border rounded-lg shadow-lg"
        >
          <EngineConnectorPanel />
        </div>
      ) : null}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
      <DialogHost />
      <DocumentPublishDialogHost />
      <FolderPublishDialogHost />
      <HistorySnapshotDialogHost />
      <HistoryUndoToastHost />
      <ToastHost />
    </div>
  );
});
